// JSON-LD Schema generators for structured data
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from './routeMeta';

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  description: 'Trusted supplier of industrial and commercial RO membranes and pressure vessels.',
  foundingDate: '2013',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'San Diego',
    addressRegion: 'CA',
    addressCountry: 'US',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-858-815-6800',
    contactType: 'sales',
    email: 'orders@aquaquote.com',
    availableLanguage: ['English'],
  },
  sameAs: [],
};

export const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/products?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

export const generateProductSchema = (product: {
  title: string;
  description?: string;
  handle: string;
  vendor?: string;
  images?: { url: string; altText?: string }[];
  priceRange?: { minVariantPrice: { amount: string; currencyCode: string } };
  availableForSale?: boolean;
}) => {
  const cleanDescription = product.description
    ? product.description.replace(/<[^>]*>/g, '').slice(0, 500)
    : `${product.title} from ${product.vendor || 'AquaQuote'}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: cleanDescription,
    url: `${SITE_URL}/product/${product.handle}`,
    image: product.images?.[0]?.url || DEFAULT_OG_IMAGE,
    brand: {
      '@type': 'Brand',
      name: product.vendor || 'AquaQuote',
    },
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/product/${product.handle}`,
      priceCurrency: product.priceRange?.minVariantPrice?.currencyCode || 'USD',
      price: product.priceRange?.minVariantPrice?.amount || '0',
      availability: product.availableForSale !== false
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
      },
    },
  };
};

export const generateArticleSchema = (post: {
  title: string;
  excerpt?: string;
  content?: string;
  slug: string;
  author?: string;
  created_at?: string;
  updated_at?: string;
  image_url?: string;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt || '',
    url: `${SITE_URL}/blog/${post.slug}`,
    image: post.image_url || DEFAULT_OG_IMAGE,
    datePublished: post.created_at || new Date().toISOString(),
    dateModified: post.updated_at || post.created_at || new Date().toISOString(),
    author: {
      '@type': 'Person',
      name: post.author || 'AquaQuote Team',
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/favicon.png`,
      },
    },
  };
};

export const generateCollectionSchema = (collection: {
  name: string;
  description: string;
  url: string;
  numberOfItems?: number;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: collection.name,
    description: collection.description,
    url: collection.url,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: collection.numberOfItems || 0,
    },
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

export const generateBreadcrumbSchema = (
  items: Array<{ name: string; url: string }>
) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};
